import React, { useEffect, useState, useCallback } from "react";
import {
    Button,
    Form,
    FormGroup,
    Label,
    Input,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Spinner,
} from "reactstrap";
import dayjs from "dayjs";
import * as Yup from "yup";
import { toast } from "react-toastify";
import {
    buildDriverDetailsFormData,
    getDriverDetails,
    normalizeDriverDetailsApiRow,
    saveDriverDetails,
} from "model/DriverDetailsModel";
import { downloadFileFromUrl } from "helpers/GeneralHelper";
import Swal from "sweetalert2";
/** True when API returned a stored file id (numeric id > 0) or a download URL. */
const hasExistingFile = (imageId, downloadUrl = "") => {
    if (String(downloadUrl ?? "").trim()) return true;
    if (imageId === null || imageId === undefined || imageId === "") return false;
    if (typeof imageId === "number") return !Number.isNaN(imageId) && imageId !== 0;
    const n = Number(imageId);
    if (Number.isFinite(n)) return n !== 0;
    return String(imageId).trim() !== "";
};

const toDateInputValue = (iso) => {
    if (!iso) return "";
    const d = dayjs(iso);
    return d.isValid() ? d.format("YYYY-MM-DD") : "";
};

const toIsoDateForApi = (dateOnly) => {
    if (!dateOnly) return "";
    const d = dayjs(dateOnly);
    return d.isValid() ? d.startOf("day").toISOString() : "";
};

const initialForm = () => ({
    requestSource: 0,
    candidateClientId: "",
    ssn: "",
    licenseNumber: "",
    licenseClass: "",
    licenseState: "",
    licenseExpiry: "",
    medicalCertExpiry: "",
    endorsements: "",
    note: "",
    ssnApprovalRequired: 0,
    licenseApprovalRequired: 0,
    medicalCertApprovalRequired: 0,
    showApproveSsn: false,
    showApproveDl: false,
    showApproveMec: false,
    approvalRequired: 0,
    pendingApprovalDocumentTypes: [],
    updatedBy: 0,
    submitValue: 0,
});

const EMPTY_IMAGE_IDS = {
    SSNImage: null,
    LicenseImage: null,
    MedicalCertImage: null,
};

const EMPTY_IMAGE_URLS = {
    SSNImage: "",
    LicenseImage: "",
    MedicalCertImage: "",
};

const EMPTY_FILES = {
    SSNImageFile: null,
    LicenseImageFile: null,
    MedicalCertImageFile: null,
};

const toDocApproveValue = (rawValue) => {
    const parsed = Number(rawValue);
    return parsed === -1 ? -1 : 1;
};

const hasDlNumber = (licenseNumber) => String(licenseNumber ?? "").trim().length > 0;

const driverDetailsSchema = Yup.object().shape({
    licenseNumber: Yup.string().trim(),
    licenseClass: Yup.string().trim().when("licenseNumber", {
        is: hasDlNumber,
        then: (schema) => schema.required("DL Class is required"),
        otherwise: (schema) => schema.notRequired(),
    }),
    licenseState: Yup.string().trim().when("licenseNumber", {
        is: hasDlNumber,
        then: (schema) => schema.required("DL Issuing State is required"),
        otherwise: (schema) => schema.notRequired(),
    }),
    licenseExpiry: Yup.string().when("licenseNumber", {
        is: hasDlNumber,
        then: (schema) => schema.required("DL Expiry is required"),
        otherwise: (schema) => schema.notRequired(),
    }),
});

const DriverDetailsModal = ({
    isOpen,
    onClose,
    candidateId,
    candidateClientId: candidateClientIdProp = "",
    onSuccess,
}) => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(initialForm);
    const [imageIds, setImageIds] = useState(EMPTY_IMAGE_IDS);
    const [imageUrls, setImageUrls] = useState(EMPTY_IMAGE_URLS);
    const [files, setFiles] = useState(EMPTY_FILES);
    const [errors, setErrors] = useState({});

    const resolveCandidateClientId = useCallback(
        (fromForm = form) =>
            String(
                candidateClientIdProp ||
                    fromForm?.candidateClientId ||
                    candidateId ||
                    ""
            ).trim(),
        [candidateClientIdProp, candidateId, form.candidateClientId]
    );

    const resetState = useCallback(() => {
        setForm(initialForm());
        setImageIds(EMPTY_IMAGE_IDS);
        setImageUrls(EMPTY_IMAGE_URLS);
        setFiles(EMPTY_FILES);
        setErrors({});
    }, []);

    const loadDetails = useCallback(async () => {
        if (!candidateId) return;
        setLoading(true);
        try {
            const res = await getDriverDetails(candidateId);
            const row = normalizeDriverDetailsApiRow(res?.data ?? res);
            const fallbackClientId = String(
                candidateClientIdProp || candidateId || ""
            ).trim();

            if (!row) {
                setForm({
                    ...initialForm(),
                    candidateClientId: fallbackClientId,
                });
                return;
            }
            const showApproveSsn = Boolean(row.showApproveSsn);
            const showApproveDl = Boolean(row.showApproveDl);
            const showApproveMec = Boolean(row.showApproveMec);
            setForm({
                ...initialForm(),
                requestSource: row.requestSource,
                candidateClientId: row.candidateClientId || fallbackClientId,
                ssn: row.ssn,
                licenseNumber: row.licenseNumber,
                licenseClass: row.licenseClass,
                licenseState: row.licenseState,
                licenseExpiry: toDateInputValue(row.licenseExpiry),
                medicalCertExpiry: toDateInputValue(row.medicalCertExpiry),
                endorsements: row.endorsements,
                note: row.note,
                ssnApprovalRequired: showApproveSsn
                    ? 1
                    : toDocApproveValue(row.ssnApprovalRequired),
                licenseApprovalRequired: showApproveDl
                    ? 1
                    : toDocApproveValue(row.licenseApprovalRequired),
                medicalCertApprovalRequired: showApproveMec
                    ? 1
                    : toDocApproveValue(row.medicalCertApprovalRequired),
                showApproveSsn,
                showApproveDl,
                showApproveMec,
                approvalRequired: row.approvalRequired,
                pendingApprovalDocumentTypes: row.pendingApprovalDocumentTypes ?? [],
                updatedBy: row.updatedBy,
                submitValue: row.submitValue,
            });
            setImageIds({
                SSNImage: row.ssnImage ?? null,
                LicenseImage: row.licenseImage ?? null,
                MedicalCertImage: row.medicalCertImage ?? null,
            });
            setImageUrls({
                SSNImage: row.ssnImageUrl ?? "",
                LicenseImage: row.licenseImageUrl ?? "",
                MedicalCertImage: row.medicalCertImageUrl ?? "",
            });
            setFiles(EMPTY_FILES);
        } catch (e) {
            toast.error(e?.message || "Unable to load driver details");
        } finally {
            setLoading(false);
        }
    }, [candidateId, candidateClientIdProp]);

    useEffect(() => {
        if (!isOpen || !candidateId) return;
        loadDetails();
    }, [isOpen, candidateId, loadDetails]);

    const validateDriverDetailsForm = useCallback(async () => {
        const nextErrors = {};

        try {
            await driverDetailsSchema.validate(form, { abortEarly: false });
        } catch (err) {
            err.inner?.forEach((error) => {
                nextErrors[error.path] = error.message;
            });
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    }, [form]);

    const handleChange = async (e) => {
        const { name, value, type, checked } = e.target;
        const next =
            type === "checkbox" ? checked : type === "number" ? Number(value) : value;
        const nextForm = { ...form, [name]: next };
        setForm(nextForm);

        if (name === "licenseNumber" && !hasDlNumber(next)) {
            setErrors((prev) => {
                const { licenseClass, licenseState, licenseExpiry, ...rest } = prev;
                return rest;
            });
            return;
        }

        if (!driverDetailsSchema.fields[name]) return;

        try {
            await driverDetailsSchema.validateAt(name, nextForm);
            setErrors((prev) => ({ ...prev, [name]: "" }));
        } catch (err) {
            setErrors((prev) => ({ ...prev, [name]: err.message }));
        }
    };

    const handleFileChange = (field) => (e) => {
        const f = e.target.files?.[0] || null;
        setFiles((prev) => ({ ...prev, [field]: f }));
    };

    const handleDocApprovalToggle = (fieldName, docLabel) => async (e) => {
        const previousValue = Number(form[fieldName]) === 1 ? 1 : -1;
        const nextValue = e.target.checked ? 1 : -1;
        const actionLabel = nextValue === 1 ? "Approve" : "Reject";
        const result = await Swal.fire({
            title: `${actionLabel} ${docLabel}?`,
            text: `Are you sure you want to ${actionLabel.toLowerCase()} ${docLabel}?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: nextValue === 1 ? "#16a34a" : "#dc3545",
            cancelButtonColor: "#6c757d",
            confirmButtonText: `Yes, ${actionLabel}`,
            target: document.body,
            allowOutsideClick: false,
            didOpen: () => {
                const el = document.querySelector(".swal2-container");
                if (el) el.style.zIndex = 20000;
            },
        });
        if (!result.isConfirmed) {
            e.target.checked = previousValue === 1;
            return;
        }
        setForm((prev) => ({ ...prev, [fieldName]: nextValue }));
    };

    const buildApprovalPayload = useCallback(
        ({ submitValueOverride, rejectAll = false } = {}) => {
            const ssnApprovalRequiredPayload = rejectAll
                ? 0
                : Boolean(form.showApproveSsn)
                  ? Number(form.ssnApprovalRequired) === 1
                      ? 1
                      : 0
                  : 0;
            const licenseApprovalRequiredPayload = rejectAll
                ? 0
                : Boolean(form.showApproveDl)
                  ? Number(form.licenseApprovalRequired) === 1
                      ? 1
                      : 0
                  : 0;
            const medicalCertApprovalRequiredPayload = rejectAll
                ? 0
                : Boolean(form.showApproveMec)
                  ? Number(form.medicalCertApprovalRequired) === 1
                      ? 1
                      : 0
                  : 0;

                  // console.log('...isApprovalRequiredMode', isApprovalRequiredMode)

            const submitValuePayload =
                submitValueOverride !== undefined
                    ? submitValueOverride
                    : (isApprovalRequiredMode && ssnApprovalRequiredPayload === 0 &&
                        licenseApprovalRequiredPayload === 0 &&
                        medicalCertApprovalRequiredPayload === 0)
                      ? -1
                      : 1;

            return {
                ssnApprovalRequired: ssnApprovalRequiredPayload,
                licenseApprovalRequired: licenseApprovalRequiredPayload,
                medicalCertApprovalRequired: medicalCertApprovalRequiredPayload,
                submitValue: submitValuePayload,
            };
        },
        [form]
    );

    const submitDriverDetails = useCallback(
        async ({ submitValueOverride, rejectAll = false } = {}) => {
            if (!candidateId) {
                toast.error("Candidate ID not found");
                return;
            }

            setSaving(true);
            try {
                const approvalPayload = buildApprovalPayload({
                    submitValueOverride,
                    rejectAll,
                });

                const body = buildDriverDetailsFormData({
                    form: {
                        ...form,
                        ...approvalPayload,
                        licenseExpiry: toIsoDateForApi(form.licenseExpiry),
                        medicalCertExpiry: toIsoDateForApi(form.medicalCertExpiry),
                    },
                    imageIds,
                    files,
                    candidateClientId: resolveCandidateClientId(form),
                });
                const res = await saveDriverDetails(candidateId, body);
                if (res?.success || res?.isSuccess || res?.status) {
                    toast.success(
                        res?.message ||
                            (rejectAll ? "Submission rejected" : "Driver details saved")
                    );
                    onSuccess?.();
                    resetState();
                    onClose?.();
                } else {
                    toast.error(
                        res?.message || res?.error?.message || "Unable to save driver details"
                    );
                }
            } catch (err) {
                toast.error(err?.message || "Unable to save driver details");
            } finally {
                setSaving(false);
            }
        },
        [
            buildApprovalPayload,
            candidateId,
            files,
            form,
            imageIds,
            onClose,
            onSuccess,
            resetState,
            resolveCandidateClientId,
        ]
    );

    const handleSubmit = async (e) => {
        e.preventDefault();
        const isValid = await validateDriverDetailsForm();
        if (!isValid) {
            toast.error("Please fix the highlighted fields");
            return;
        }
        await submitDriverDetails();
    };

    const handleRejectAll = async () => {
        const result = await Swal.fire({
            title: "Reject all documents?",
            text: "Are you sure you want to reject all pending document submissions?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#dc3545",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Yes, Reject All",
            target: document.body,
            allowOutsideClick: false,
            didOpen: () => {
                const el = document.querySelector(".swal2-container");
                if (el) el.style.zIndex = 20000;
            },
        });
        if (!result.isConfirmed) return;
        await submitDriverDetails({ submitValueOverride: -1, rejectAll: true });
    };

    const handleClose = () => {
        if (!saving) {
            resetState();
            onClose?.();
        }
    };

    const renderDownload = (fileFieldKey, imageKey) => {
        if (files[fileFieldKey]) return null;
        const downloadUrl = String(imageUrls[imageKey] ?? "").trim();
        if (!hasExistingFile(imageIds[imageKey], downloadUrl)) return null;
        return (
            <button
                type="button"
                className="cd-profile-image-view-link cd-profile-image-view-link--no-offset"
                title="Download file"
                onClick={() => {
                    if (downloadUrl) {
                        downloadFileFromUrl(downloadUrl, { openInNewTabOnFailure: false });
                        return;
                    }
                    toast.error("File not available for download.");
                }}
            >
                <span
                    className="cd-profile-image-view-link__icon"
                    aria-hidden="true"
                >
                    <i className="fa fa-file" />
                </span>
                <span className="cd-profile-image-view-link__text">
                    Download file
                </span>
                <i
                    className="fa fa-external-link-alt cd-profile-image-view-link__external"
                    aria-hidden="true"
                />
            </button>
        );
    };

    const renderDocApprovalSwitch = (fieldName, label) => {
        const checked = Number(form[fieldName]) === 1;
        return (
            <div className="driver-doc-approval-switch">
                <span className={`driver-doc-approval-switch__state ${checked ? "is-approve" : "is-reject"}`}>
                    {checked ? "Approve" : "Reject"}
                </span>
                <div className="form-switch mb-0">
                    <Input
                        type="switch"
                        className="form-check-input-padding"
                        key={`${fieldName}-${String(form[fieldName])}`}
                        defaultChecked={checked}
                        onChange={handleDocApprovalToggle(fieldName, label)}
                    />
                </div>
            </div>
        );
    };

    const isApprovalRequiredMode = Number(form.approvalRequired) === 1;
    const pendingApprovalDocumentTypesLabel = Array.isArray(form.pendingApprovalDocumentTypes)
        ? form.pendingApprovalDocumentTypes.join(", ")
        : "";
    const hasPendingApprovalDocuments =
        Array.isArray(form.pendingApprovalDocumentTypes) &&
        form.pendingApprovalDocumentTypes.length > 0;
    const showReviewBanner = isApprovalRequiredMode && hasPendingApprovalDocuments;

    const renderFileActions = (
        fileFieldKey,
        imageKey,
        approveField,
        label,
        shouldShowApprove
    ) => {
        const downloadButton = renderDownload(fileFieldKey, imageKey);
        if (!downloadButton && !shouldShowApprove) return null;
        return (
            <div className="driver-doc-actions-row">
                <div className="driver-doc-actions-row__download">{downloadButton}</div>
                {shouldShowApprove ? renderDocApprovalSwitch(approveField, label) : null}
            </div>
        );
    };

    return (
        <Modal
            isOpen={isOpen}
            toggle={handleClose}
            size="md"
            scrollable
            backdrop="static"
            centered
            className="modal-full-height"
        >
            <ModalHeader toggle={handleClose}>DRIVER DETAILS</ModalHeader>
            <ModalBody>
                {loading ? (
                    <div className="d-flex justify-content-center py-5">
                        <Spinner color="primary" />
                    </div>
                ) : (
                    <Form id="driver-details-form" onSubmit={handleSubmit}>
                        {showReviewBanner ? (
                            <div className="driver-details-review-banner text-center">
                                <p className="mb-0 text-danger">
                                    Please Download the document and review it before approving
                                    or rejecting the submission.
                                    {pendingApprovalDocumentTypesLabel ? (
                                        <>
                                            {" "}
                                            <span className="fw-semibold">
                                                ({pendingApprovalDocumentTypesLabel})
                                            </span>
                                        </>
                                    ) : null}
                                </p>
                            </div>
                        ) : null}
                        <FormGroup>
                            <Label>
                                SSN
                            </Label>
                            <Input name="ssn" value={form.ssn} onChange={handleChange} />
                        </FormGroup>
                        <FormGroup>
                            <Label>SSN File (doc, pdf, docx, image)</Label>
                            <Input
                                type="file"
                                accept=".doc,.docx,.pdf,image/*"
                                onChange={handleFileChange("SSNImageFile")}
                            />
                            {renderFileActions(
                                "SSNImageFile",
                                "SSNImage",
                                "ssnApprovalRequired",
                                "SSN file",
                                Boolean(form.showApproveSsn)
                            )}
                        </FormGroup>

                        <FormGroup>
                            <Label>
                                DL Number
                            </Label>
                            <Input
                                name="licenseNumber"
                                value={form.licenseNumber}
                                onChange={handleChange}
                            />
                            {errors.licenseNumber ? (
                                <small className="text-danger">{errors.licenseNumber}</small>
                            ) : null}
                        </FormGroup>
                        <FormGroup>
                            <Label>
                                DL Class
                            </Label>
                            <Input
                                name="licenseClass"
                                value={form.licenseClass}
                                onChange={handleChange}
                            />
                            {errors.licenseClass ? (
                                <small className="text-danger">{errors.licenseClass}</small>
                            ) : null}
                        </FormGroup>
                        <FormGroup>
                            <Label>
                                DL Issuing State
                            </Label>
                            <Input
                                name="licenseState"
                                value={form.licenseState}
                                onChange={handleChange}
                            />
                            {errors.licenseState ? (
                                <small className="text-danger">{errors.licenseState}</small>
                            ) : null}
                        </FormGroup>
                        <FormGroup>
                            <Label>
                                DL Expiry
                            </Label>
                            <Input
                                type="date"
                                name="licenseExpiry"
                                value={form.licenseExpiry}
                                onChange={handleChange}
                            />
                            {errors.licenseExpiry ? (
                                <small className="text-danger">{errors.licenseExpiry}</small>
                            ) : null}
                        </FormGroup>
                        <FormGroup>
                            <Label>DL File (doc, pdf, docx, image)</Label>
                            <Input
                                type="file"
                                accept=".doc,.docx,.pdf,image/*"
                                onChange={handleFileChange("LicenseImageFile")}
                            />
                            {renderFileActions(
                                "LicenseImageFile",
                                "LicenseImage",
                                "licenseApprovalRequired",
                                "DL file",
                                Boolean(form.showApproveDl)
                            )}
                        </FormGroup>

                        <FormGroup>
                            <Label>
                                MEC Expiry
                            </Label>
                            <Input
                                type="date"
                                name="medicalCertExpiry"
                                value={form.medicalCertExpiry}
                                onChange={handleChange}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>MEC File (doc, pdf, docx, image)</Label>
                            <Input
                                type="file"
                                accept=".doc,.docx,.pdf,image/*"
                                onChange={handleFileChange("MedicalCertImageFile")}
                            />
                            {renderFileActions(
                                "MedicalCertImageFile",
                                "MedicalCertImage",
                                "medicalCertApprovalRequired",
                                "MEC file",
                                Boolean(form.showApproveMec)
                            )}
                        </FormGroup>

                        <FormGroup>
                            <Label>
                                Endorsement
                            </Label>
                            <Input
                                name="endorsements"
                                value={form.endorsements}
                                onChange={handleChange}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>
                                Notes
                            </Label>
                            <Input
                                type="textarea"
                                name="note"
                                rows={4}
                                value={form.note}
                                onChange={handleChange}
                            />
                        </FormGroup>
                    </Form>
                )}
            </ModalBody>
            {!loading && (
                <ModalFooter className="d-flex justify-content-between flex-wrap gap-2">
                    {/* <Button
                        color="secondary"
                        type="button"
                        onClick={handleClose}
                        disabled={saving}
                    >
                        Close
                    </Button> */}
                    <div className="d-flex gap-2 ms-auto">
                        {isApprovalRequiredMode ? (
                            <Button
                                color="danger"
                                type="button"
                                outline
                                onClick={() => {
                                    void handleRejectAll();
                                }}
                                disabled={saving}
                            >
                                <i className="fa fa-times me-1" aria-hidden />
                                Reject All
                            </Button>
                        ) : null}
                        <Button
                            color="primary"
                            type="submit"
                            form="driver-details-form"
                            disabled={saving}
                        >
                            {isApprovalRequiredMode ? (
                                <>
                                    <i className="fa fa-check me-1" aria-hidden />
                                    {saving ? "Approving…" : "Approve"}
                                </>
                            ) : saving ? (
                                "Saving…"
                            ) : (
                                "Save"
                            )}
                        </Button>
                    </div>
                </ModalFooter>
            )}
        </Modal>
    );
};

export default DriverDetailsModal;
